/**
 * @fileoverview Invitation-link validity query hook.
 * @description Wraps GET-on-mount validation of the token from the /invitation/accept URL. No
 * retry: a 400 / appCode 1040 is a final answer about this link, not something that resolves
 * itself on a second try (a rate-limited or unavailable check can be retried from the page).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { validateInvitation } from '@/features/auth/services/invitation.service'

import type { InvitationValidationResponse } from '@/features/auth/services/invitation.service'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Checks whether an invitation token is still usable.
 * @param {string} token - The token from the /invitation/accept URL's `token` query param.
 * @returns {ReturnType<typeof useQuery<InvitationValidationResponse, ApiError>>} The query object
 * (data, isPending, isError, ...).
 */
export function useValidateInvitation(token: string) {
  return useQuery<InvitationValidationResponse, ApiError>({
    queryKey: ['identity', 'invitations', 'validate', token],
    queryFn: () => validateInvitation(token),
    enabled: token.length > 0,
    retry: false,
  })
}
